import React, { useEffect, useRef, useState } from "react";
import { Button, Table } from "semantic-ui-react";
import format from "date-fns/format";
import { openModal } from "../../../app/common/modals/modalReducer";
import { getUnassignedLeads } from "../leadActions";
import { useDispatch } from "react-redux";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import subYears from "date-fns/subYears";
import firebase from "../../../app/config/firebase";
const firestore = firebase.firestore();

export default function LeadsDashboardPage() {
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const DUPE_CACHE_KEY = "dupeCache";
  const DUPE_CACHE_TTL_DAYS = 1; // or 7 for weekly
  const DUPE_CACHE_TTL_HOURS = 0.5; // refresh every 30 minutes
  //const DUPE_CACHE_TTL_MINUTES = 30;
  const dispatch = useDispatch();

  const sanitize = (val) => (val || "").trim().toLowerCase().replace(/\s+/g, "");
  const debugDupes =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("debugDupes") === "1";

  useEffect(() => {
    const unsubscribe = firestore
      .collection("leads")
      .where("status", "==", "Unassigned")
      .orderBy("createDate", "desc")
      .onSnapshot(async (snapshot) => {
        const rawLeads = snapshot.docs.map((doc) => {
          // console.log("[unassigned] doc", doc.data());
          return ({ id: doc.id, ...doc.data() })
        });


        // Step 1: Sanitize and gather all unique values
        const emails = new Set();
        const phones = new Set();
        const neededKeys = new Set();
        // Common placeholders that can cause massive false-positive duplicate matches.
        const INVALID_PHONES = new Set(["tba", "unknown", "0000000000", "n/a", "", "0", "none", "-", "na"]);

        rawLeads.forEach((lead) => {
          const email = sanitize(lead.client_email);
          let phone = sanitize(lead.client_phone);
          if (INVALID_PHONES.has(phone)) phone = "";
          const cacheKey = `e:${email}|p:${phone}`;
          // const isInvalidPhone = INVALID_PHONES.has(phone);
          // const emailValid = !!email;
          // const phoneValid = !!phone && !isInvalidPhone;

          // if (!dupeCache.current.has(cacheKey)) {
          //   if (emailValid) emails.add(email);
          //   if (phoneValid) phones.add(phone);

          //   if (emailValid || phoneValid) {
          //     neededKeys.add(cacheKey);
          //   }
          // }
          // did not exclude tba phone numbers
          if (!dupeCache.current.has(cacheKey)) {
            if (email) emails.add(email);
            if (phone) phones.add(phone);
            neededKeys.add(cacheKey);
          }
        });
        // Step 2: Firestore batch queries
        const buildQuery = (field, value) =>
          firestore
            .collection("leads")
            .where(field, "==", value)
            .get();

        const queries = [...emails].map((e) => buildQuery("client_email", e))
          .concat(
            [...phones].map((p) => buildQuery("client_phone", p))
          );

        const results = await Promise.all(queries);

        // Step 3: Build maps
        const dupeMapLists = {}; // key = email/phone, value = array of doc IDs
        const assignedMap = {};

        results.forEach((snap) => {
          snap.docs.forEach((doc) => {
            const data = doc.data();
            if (data.status === "Archived") return;
            const id = doc.id;
            const email = sanitize(data.client_email);
            let phone = sanitize(data.client_phone);
            if (INVALID_PHONES.has(phone)) phone = "";

            if (email) {
              dupeMapLists[email] = dupeMapLists[email] || [];
              dupeMapLists[email].push(id);
              if (data.status === "Assigned") assignedMap[email] = true;
            }

            if (phone) {
              dupeMapLists[phone] = dupeMapLists[phone] || [];
              dupeMapLists[phone].push(id);
              if (data.status === "Assigned") assignedMap[phone] = true;
            }
          });
        });

        // Update local cache with new results
        rawLeads.forEach((lead) => {
          const email = sanitize(lead.client_email);
          let phone = sanitize(lead.client_phone);
          if (INVALID_PHONES.has(phone)) phone = "";
          const cacheKey = `e:${email}|p:${phone}`;

          if (!neededKeys.has(cacheKey)) return; // skip if already cached

          const relatedDocIds = new Set();

          if (dupeMapLists[email]) {
            dupeMapLists[email].forEach((id) => {
              if (id !== lead.id) relatedDocIds.add(id);
            });
          }

          if (dupeMapLists[phone]) {
            dupeMapLists[phone].forEach((id) => {
              if (id !== lead.id) relatedDocIds.add(id);
            });
          }

          const dupes = relatedDocIds.size;
          const wasAssigned = assignedMap[email] || assignedMap[phone] || false;

          if (debugDupes && dupes >= 20) {
            console.log("[unassigned dupes] large dupes", {
              leadId: lead.id,
              name: lead.client_name,
              email,
              phone,
              dupes,
              emailMatches: (dupeMapLists[email] || []).length,
              phoneMatches: (phone && dupeMapLists[phone] ? dupeMapLists[phone].length : 0),
            });
          }

          dupeCache.current.set(cacheKey, {
            dupes,
            wasPreviouslyAssigned: wasAssigned,
            timestamp: Date.now(), // optional TTL support
          });
        });

        // Optional: Visual duplicates (local scan only)
        const emailVisuals = {};
        const phoneVisuals = {};

        rawLeads.forEach((lead) => {
          const email = sanitize(lead.client_email);
          let phone = sanitize(lead.client_phone);
          if (INVALID_PHONES.has(phone)) phone = "";
          if (email) emailVisuals[email] = (emailVisuals[email] || 0) + 1;
          if (phone) phoneVisuals[phone] = (phoneVisuals[phone] || 0) + 1;
        });

        if (debugDupes) {
          const top = (obj, min = 5, limit = 10) =>
            Object.entries(obj)
              .filter(([, count]) => count >= min)
              .sort((a, b) => b[1] - a[1])
              .slice(0, limit);

          console.log("[unassigned dupes] top emails", top(emailVisuals));
          console.log("[unassigned dupes] top phones", top(phoneVisuals));
        }

        // Final lead mapping
        const finalLeads = rawLeads.map((lead) => {
          const email = sanitize(lead.client_email);
          let phone = sanitize(lead.client_phone);
          if (INVALID_PHONES.has(phone)) phone = "";
          const cacheKey = `e:${email}|p:${phone}`;
          const cached = dupeCache.current.get(cacheKey) || {};

          const hasVisualDuplicate =
            (email && emailVisuals[email] > 1) ||
            (phone && phoneVisuals[phone] > 1);

          return {
            ...lead,
            dupes: cached.dupes || 0,
            wasPreviouslyAssigned: cached.wasPreviouslyAssigned || false,
            hasVisualDuplicate,
          };
        });
        // console.log("[unassigned] emails to query", [...emails]);
        // console.log("[unassigned] phones to query", [...phones]);
        // console.log("[unassigned] cache keys to check", [...neededKeys]);
        // console.log("[unassigned] final leads", finalLeads);
        // console.log("[unassigned] dupe cache content", [...dupeCache.current.entries()]);
        setLeads(finalLeads);
        setLoadingLeads(false);
      });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    return () => {
      saveDupeCache(dupeCache.current);
    };
  }, []);

  function loadDupeCache() {
    const raw = sessionStorage.getItem(DUPE_CACHE_KEY);
    if (!raw) return new Map();

    try {
      const { timestamp, entries } = JSON.parse(raw);
      const age = Date.now() - timestamp;
      // const days = age / (1000 * 60 * 60 * 24);
      // if (days > DUPE_CACHE_TTL_DAYS) {
      //     sessionStorage.removeItem(DUPE_CACHE_KEY);
      //     return new Map();
      // }
      // const minutes = age / (1000 * 60);
      // if (minutes > DUPE_CACHE_TTL_MINUTES) {
      //     sessionStorage.removeItem(DUPE_CACHE_KEY);
      //     console.log('removing cache');
      //     return new Map();
      // }
      const parsed = JSON.parse(raw);
      const ageHours = (Date.now() - parsed.timestamp) / (1000 * 60 * 60);

      if (ageHours > DUPE_CACHE_TTL_HOURS) {
        sessionStorage.removeItem("dupeCache");
        return new Map(); // expired
      }
      return new Map(entries);
    } catch {
      return new Map();
    }
  }
  const dupeCache = useRef(loadDupeCache());
  function saveDupeCache(map) {
    const payload = {
      timestamp: Date.now(),
      entries: Array.from(map.entries()),
    };

    sessionStorage.setItem(DUPE_CACHE_KEY, JSON.stringify(payload));
  }

  return (
    <div>
      <Button color="orange" onClick={() => dispatch(openModal({ modalType: "LeadCreateModal", modalProps: { status: "Unassigned" } }))}>
        Add Unassigned Lead
      </Button>

      {loadingLeads === true ? (
        <LoadingComponent />
      ) : leads && leads.length > 0 ? (
        <Table celled selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Enquired On</Table.HeaderCell>
              <Table.HeaderCell>Division</Table.HeaderCell>
              <Table.HeaderCell>Form Name</Table.HeaderCell>
              <Table.HeaderCell>Client Name</Table.HeaderCell>
              <Table.HeaderCell>Client Email</Table.HeaderCell>
              <Table.HeaderCell>Duplicates</Table.HeaderCell>
              <Table.HeaderCell>Destination</Table.HeaderCell>
              <Table.HeaderCell>Package</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {leads.map((lead) => {
              // console.log("[unassigned] lead row", lead);
              let bColor = "";
              let dColor = "inherit";
              if (lead.dupes) {
                //bColor = "#f4ffe5";
                dColor = "red";
              }
              if (lead.hasVisualDuplicate) {
                //bColor = "#fff4e5";
                dColor = "red";
              }
              if (lead.wasPreviouslyAssigned) {
                //bColor = "#fff4e5";
                dColor = "red";
              }

              return (
                <Table.Row
                  key={lead.id}
                  onClick={() => dispatch(openModal({ modalType: "LeadUnassViewModal", modalProps: { status: "Unassigned", lead: lead } }))}
                  style={{
                    cursor: "pointer",
                    backgroundColor: bColor,
                    color: dColor
                  }}
                >
                  <Table.Cell>{lead.createDate ? format(lead.createDate.toDate(), "dd MMM yyyy HH:mm a") : ""}</Table.Cell>
                  <Table.Cell>{lead.website_division}</Table.Cell>
                  <Table.Cell>{lead.form_name}</Table.Cell>
                  <Table.Cell>{lead.client_name}</Table.Cell>
                  <Table.Cell>{lead.client_email}</Table.Cell>
                  <Table.Cell>{lead.dupes}</Table.Cell>
                  <Table.Cell>{lead.destination}</Table.Cell>
                  <Table.Cell>{lead.package}</Table.Cell>
                </Table.Row>
              )
            })}
          </Table.Body>
        </Table>
      ) : (
        <div>No Leads found.</div>
      )}
    </div>
  );
}
